'use client'

import { type RefObject, useCallback, useEffect, useMemo } from 'react'
import { type Control, useWatch } from 'react-hook-form'

import { useShallow } from 'zustand/react/shallow'

import {
  ALL_PROJECT_KEYS_VALUE,
  useProjectKeyEnvironmentFilterStore,
} from '@modules/project-keys/hooks/useProjectKeyEnvironmentFilterStore'

import { useGatewayTimeRangeFilterUiConfig } from '../../hooks/useGatewayTimeRangeFilterUiConfig'
import { useMobileFiltersTimeRangeState } from '../../hooks/useMobileFiltersTimeRangeState'
import { coerceGatewayListFilterSelections } from '../domain/coerceGatewayListFilterSelections'
import { useGatewayListMobileFiltersStore } from '../store/gateway-list-mobile-filters.store'
import type { GatewayListFilterTraitsSource } from '../types/gatewayListFilters.types'

import { useGatewayListFilterOptionSets } from './useGatewayListFilterOptionSets'

export type GatewayListMobileFiltersApplyValues = {
  status: string
  runtime: string
  feed: string
}

export type GatewayListMobileFiltersControlValues = GatewayListMobileFiltersApplyValues & {
  search?: string
  projectKey?: string
  dateFrom?: string
  dateTo?: string
  hostedRuntimes?: string[]
  statuses?: string[]
}

type UseGatewayListMobileFiltersParams<TFieldValues extends GatewayListMobileFiltersControlValues> = {
  isOpen: boolean
  control: Control<TFieldValues>
  customGroupRef: RefObject<HTMLDivElement | null>
  filterSources: GatewayListFilterTraitsSource[]
  onApplyFilters: (values: GatewayListMobileFiltersApplyValues) => void
  onClose: () => void
}

export function useGatewayListMobileFilters<TFieldValues extends GatewayListMobileFiltersControlValues>({
  isOpen,
  control,
  customGroupRef,
  filterSources,
  onApplyFilters,
  onClose,
}: UseGatewayListMobileFiltersParams<TFieldValues>) {
  const { options: timeRangeOptions, labels: timeRangeLabels } = useGatewayTimeRangeFilterUiConfig()
  const {
    options: environmentOptions,
    value: environmentValue,
    applyValue: onEnvironmentApply,
  } = useProjectKeyEnvironmentFilterStore()

  const {
    openMenu,
    pending,
    setOpenMenu,
    setPendingStatus,
    setPendingRuntime,
    setPendingFeed,
    setPendingEnvironment,
    hydratePending,
    applyCoercedPending,
    resetPending,
  } = useGatewayListMobileFiltersStore(
    useShallow((state) => ({
      openMenu: state.openMenu,
      pending: state.pending,
      setOpenMenu: state.setOpenMenu,
      setPendingStatus: state.setPendingStatus,
      setPendingRuntime: state.setPendingRuntime,
      setPendingFeed: state.setPendingFeed,
      setPendingEnvironment: state.setPendingEnvironment,
      hydratePending: state.hydratePending,
      applyCoercedPending: state.applyCoercedPending,
      resetPending: state.resetPending,
    }))
  )

  const { status: pendingStatus, runtime: pendingRuntime, feed: pendingFeed, environment: pendingEnvironment } = pending

  const formValues = useWatch({ control })

  const environmentSelectOptions = useMemo(
    () => environmentOptions.map((option) => ({ id: option.value, label: option.label })),
    [environmentOptions]
  )

  const closeAllMenus = useCallback(() => setOpenMenu(null), [setOpenMenu])

  const {
    applyPendingTimeRangeToStore,
    canApplyCustomRange,
    handleSelectTimeRange,
    menuItems,
    pendingCustomRange,
    pendingTimeRange,
    resetPendingTimeRange,
    setEndDate,
    setEndTime,
    setStartDate,
    setStartTime,
    showCustom,
    timeRangeTriggerLabel,
  } = useMobileFiltersTimeRangeState({
    isOpen,
    customGroupRef,
    timeRangeOptions,
    customFallbackLabel: timeRangeLabels.triggerCustomFallback,
    closeAllMenus,
  })

  const {
    statusOptions,
    runtimeOptions,
    moduleOptions: feedOptions,
  } = useGatewayListFilterOptionSets(filterSources, {
    status: pendingStatus,
    runtime: pendingRuntime,
    feed: pendingFeed,
    projectKey: pendingEnvironment,
  })

  useEffect(() => {
    const updates = coerceGatewayListFilterSelections(
      { status: pendingStatus, runtime: pendingRuntime, feed: pendingFeed, projectKey: pendingEnvironment },
      {
        status: statusOptions,
        runtime: runtimeOptions,
        module: feedOptions,
        projectKey: environmentSelectOptions,
      }
    )

    if (Object.keys(updates).length > 0) {
      applyCoercedPending(updates)
    }
  }, [
    applyCoercedPending,
    environmentSelectOptions,
    feedOptions,
    pendingEnvironment,
    pendingFeed,
    pendingRuntime,
    pendingStatus,
    runtimeOptions,
    statusOptions,
  ])

  const statusTriggerLabel = statusOptions.find((option) => option.id === pendingStatus)?.label ?? ''
  const runtimeTriggerLabel = runtimeOptions.find((option) => option.id === pendingRuntime)?.label ?? ''
  const feedTriggerLabel = feedOptions.find((option) => option.id === pendingFeed)?.label ?? ''
  const environmentTriggerLabel =
    environmentSelectOptions.find((option) => option.id === pendingEnvironment)?.label ?? ''

  const handleReset = () => {
    resetPendingTimeRange()
    resetPending(environmentOptions[0]?.value ?? ALL_PROJECT_KEYS_VALUE)
  }

  const handleApply = useCallback(() => {
    if (!applyPendingTimeRangeToStore()) return

    onEnvironmentApply(pendingEnvironment)
    onApplyFilters({
      status: pendingStatus,
      runtime: pendingRuntime,
      feed: pendingFeed,
    })
    onClose()
  }, [
    applyPendingTimeRangeToStore,
    onApplyFilters,
    onClose,
    onEnvironmentApply,
    pendingEnvironment,
    pendingFeed,
    pendingRuntime,
    pendingStatus,
  ])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    hydratePending({
      status: formValues?.status ?? 'all',
      runtime: formValues?.runtime ?? 'all',
      feed: formValues?.feed ?? 'all',
      environment: environmentValue,
    })
  }, [environmentValue, formValues?.feed, formValues?.runtime, formValues?.status, hydratePending, isOpen])

  return {
    canApplyCustomRange,
    environmentSelectOptions,
    environmentTriggerLabel,
    feedOptions,
    feedTriggerLabel,
    handleApply,
    handleReset,
    handleSelectTimeRange,
    menuItems,
    openMenu,
    pendingCustomRange,
    pendingEnvironment,
    pendingFeed,
    pendingRuntime,
    pendingStatus,
    pendingTimeRange,
    runtimeOptions,
    runtimeTriggerLabel,
    setEndDate,
    setEndTime,
    setOpenMenu,
    setPendingEnvironment,
    setPendingFeed,
    setPendingRuntime,
    setPendingStatus,
    setStartDate,
    setStartTime,
    showCustom,
    statusOptions,
    statusTriggerLabel,
    timeRangeLabels,
    timeRangeTriggerLabel,
  }
}
